import { FormField, NewDateRangeInput } from '@george-labs.com/design-system';
import type { ComponentProps } from 'react';

import { useField } from '@luna/form2';
import { isValidDate } from '@luna/date';

import type { FormFieldProps } from './types/FormFieldProps';

interface Props extends Omit<FormFieldProps, 'fieldName'> {
    startDateFieldName: string;
    endDateFieldName: string;
    'data-cy'?: string;
    isOutSideRange?: (date: Date) => boolean;
    startId?: string;
    endId?: string;
    placeholderStart?: string;
    placeholderEnd?: string;
    label: string;
    inputProps?: Partial<ComponentProps<typeof NewDateRangeInput>>;
    shouldClearOnUnmount?: boolean;
}

export const FormDateRangeInput = ({
    label,
    labelPosition,
    'data-cy': dataCy,
    required,
    startDateFieldName,
    endDateFieldName,
    isOutSideRange,
    startId,
    endId,
    placeholderStart,
    placeholderEnd,
    description,
    labelColumnWidth,
    inputProps,
    warning,
    info,
    alert,
    addon,
    shouldClearOnUnmount,
}: Props) => {
    const [
        { value: startDateValue, onBlur: onStartDateBlur },
        { touched: startDateTouched, error: startDateError },
        { setValue: setStartDateValue },
    ] = useField(startDateFieldName);
    const [
        { value: endDateValue, onBlur: onEndDateBlur },
        { touched: endDateTouched, error: endDateError },
        { setValue: setEndDateValue },
    ] = useField(endDateFieldName, { shouldClearOnUnmount });

    const handleClose = () => {
        onStartDateBlur();
        onEndDateBlur();
    };
    const handleBlur = (value: string) => {
        if (inputProps?.memorable) {
            handleClose();
        }
        inputProps?.onBlur?.(value);
    };

    const handleDatesChange = (newDates: { startDate: Date | null; endDate: Date | null }) => {
        if (!newDates.startDate) {
            setStartDateValue(null);
        }

        if (newDates.startDate && isValidDate(newDates.startDate)) {
            setStartDateValue(newDates.startDate?.toISOString());
        }

        if (!newDates.endDate) {
            setEndDateValue(null);
        }

        if (newDates.endDate && isValidDate(newDates.endDate)) {
            setEndDateValue(newDates.endDate.toISOString());
        }
    };

    const startDate = startDateValue ? new Date(startDateValue) : undefined;
    const endDate = endDateValue ? new Date(endDateValue) : undefined;

    return (
        <FormField
            required={required}
            label={label}
            labelPosition={labelPosition}
            error={(startDateTouched && startDateError) || (endDateTouched && endDateError)}
            input={
                <NewDateRangeInput
                    {...inputProps}
                    data-cy={dataCy}
                    startId={startId}
                    endId={endId}
                    startDate={startDate}
                    endDate={endDate}
                    minimumNights={0}
                    placeholderStart={placeholderStart || ''}
                    placeholderEnd={placeholderEnd || ''}
                    onDatesChange={handleDatesChange}
                    isOutsideRange={isOutSideRange}
                    onClose={handleClose}
                    onBlur={handleBlur}
                />
            }
            description={description}
            labelColumnWidth={labelColumnWidth}
            warning={warning}
            info={info}
            alert={alert}
            addon={addon}
        />
    );
};

FormDateRangeInput.LABEL_POSITION = FormField.LABEL_POSITION;
